// Utilidades compartidas del módulo Leads (evita duplicar en LeadsIndex/LeadDetail/LeadCard).
import LeadTasksAPI from 'dashboard/api/leadTasks';
import startOfToday from 'date-fns/startOfToday';
import endOfToday from 'date-fns/endOfToday';
import endOfTomorrow from 'date-fns/endOfTomorrow';
import endOfWeek from 'date-fns/endOfWeek';
import startOfWeek from 'date-fns/startOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import isSameDay from 'date-fns/isSameDay';
import { es as dateFnsEs } from 'date-fns/locale';

// Cantidad de leads por página/columna en el tablero (debe coincidir con
// LeadsController::PER_PAGE en el backend).
export const LEADS_PER_PAGE = 25;

// Desenvuelve respuestas {payload:[...]} o el dato directo.
export const unwrap = data => data?.payload ?? data ?? [];

// Formato de moneda para presupuestos (Intl, es-EC, sin decimales).
export const formatLeadCurrency = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (error) {
    return `${currency || 'USD'} ${amount}`;
  }
};

// Descarga un Blob como archivo (export CSV).
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Acciones reveladas por hover SOLO donde hay hover real (mouse): en táctil
// quedan siempre visibles y el foco de teclado también las revela. Receta
// ÚNICA compartida (AgendaTaskRow + LeadContactPanel) para que no driftee;
// puede vivir en este .js porque routes/dashboard/**/*.js está en el content
// de Tailwind. focus-visible es inerte en contenedores (no enfocables).
export const HOVER_REVEAL_CLASS =
  '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100 focus-visible:opacity-100';

// Locale es del datepicker (vue-datepicker-next, prop :lang como OBJETO),
// DERIVADO de date-fns/locale/es — la misma fuente que renderiza el resto de
// las fechas del módulo (no una tabla a mano que driftee). NO importar
// 'vue-datepicker-next/locale/es': el import estático se iza al ENTRY y rompe
// el boot; el dinámico dispara el guard de chunkReload (incidentes 2026-07-02).
const esMonths = width =>
  Array.from({ length: 12 }, (_, i) => dateFnsEs.localize.month(i, { width }));
const esDays = width =>
  Array.from({ length: 7 }, (_, i) => dateFnsEs.localize.day(i, { width }));
export const DATE_PICKER_LANG_ES = {
  formatLocale: {
    months: esMonths('wide'),
    monthsShort: esMonths('abbreviated'),
    weekdays: esDays('wide'),
    weekdaysShort: esDays('abbreviated'),
    weekdaysMin: esDays('short'),
    firstDayOfWeek: 1,
    firstWeekContainsDate: 1,
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
};

// Señal de urgencia de la próxima tarea del lead (estilo Kommo):
// sin tarea → 'amber' · vencida → 'ruby' · hoy → 'teal' · futura → null (sin color urgente).
export const taskColor = nextTaskAt => {
  if (!nextTaskAt) return 'amber';
  const due = new Date(nextTaskAt);
  const now = new Date();
  if (due < now) return 'ruby';
  if (due.toDateString() === now.toDateString()) return 'teal';
  return null;
};

// ---- Agenda (Fase 3) ----

// Clase de fondo del punto de urgencia (misma semántica que taskColor()).
const DOT_CLASSES = {
  ruby: 'bg-n-ruby-9',
  teal: 'bg-n-teal-9',
  amber: 'bg-n-amber-9',
};
export const taskDotClass = dueAt =>
  DOT_CLASSES[taskColor(dueAt)] || 'bg-n-slate-6';

// Buckets del tablero de agenda (estilo Kommo §6.5), en orden.
export const AGENDA_BUCKETS = ['overdue', 'today', 'tomorrow', 'week', 'later'];

// Bucket temporal de una tarea pendiente según due_at (semana = lunes a domingo).
export const bucketForTask = dueAt => {
  if (!dueAt) return 'later';
  const due = new Date(dueAt);
  if (due < startOfToday()) return 'overdue';
  if (due <= endOfToday()) return 'today';
  if (due <= endOfTomorrow()) return 'tomorrow';
  if (due <= endOfWeek(new Date(), { weekStartsOn: 1 })) return 'week';
  return 'later';
};

// Agrupa una lista de tareas pendientes en { overdue:[], today:[], ... }.
export const groupTasksByBucket = tasks => {
  const groups = Object.fromEntries(AGENDA_BUCKETS.map(b => [b, []]));
  tasks.forEach(task => groups[bucketForTask(task.due_at)].push(task));
  return groups;
};

// Matriz de semanas (lunes→domingo) que cubre el mes de `monthDate`.
export const monthMatrix = monthDate => {
  const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
};

export const tasksForDay = (tasks, day) =>
  tasks.filter(task => task.due_at && isSameDay(new Date(task.due_at), day));

// ---- Widgets del tablero ----

// Rango ISO {from,to} para un periodo de widget (navegación al tablero filtrado).
export const PERIOD_OPTIONS = ['today', 'yesterday', 'week', 'month'];

export const periodDateRange = (period = 'today') => {
  const now = new Date();
  switch (period) {
    case 'yesterday': {
      const y = subDays(now, 1);
      return {
        from: startOfDay(y).toISOString(),
        to: endOfDay(y).toISOString(),
      };
    }
    case 'week':
      return {
        from: startOfWeek(now).toISOString(),
        to: endOfWeek(now).toISOString(),
      };
    case 'month':
      return {
        from: startOfMonth(now).toISOString(),
        to: endOfMonth(now).toISOString(),
      };
    default:
      return {
        from: startOfDay(now).toISOString(),
        to: endOfDay(now).toISOString(),
      };
  }
};

// Trae TODAS las tareas que cumplen `params`, paginando (PER_PAGE backend = 50).
// Evita truncar silenciosamente la agenda; tope duro de 40 páginas por seguridad.
export const fetchAllTasks = async (params = {}) => {
  const all = [];
  let page = 1;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await LeadTasksAPI.get({ ...params, page });
    const batch = unwrap(data);
    all.push(...batch);
    if (batch.length < 50) break;
    if (page >= 40) {
      // eslint-disable-next-line no-console
      console.warn(
        '[agenda] fetchAllTasks reached the 40-page cap; some tasks may be omitted.'
      );
      break;
    }
    page += 1;
  }
  return all;
};

// Reloj de espera de la bandeja de Entrantes: cuánto lleva un lead sin triage.
// Escalada: <1 h "ahora" · <24 h en horas · 1-3 d ámbar · >3 d rojo.
// Recibe epoch en SEGUNDOS (como serializa el jbuilder los timestamps).
export const waitingSince = epochSeconds => {
  if (!epochSeconds) return null;
  const hours = Math.floor((Date.now() / 1000 - epochSeconds) / 3600);
  if (hours < 1) return { unit: 'now', n: 0, tone: 'slate' };
  if (hours < 24) return { unit: 'hours', n: hours, tone: 'slate' };
  const days = Math.floor(hours / 24);
  return { unit: 'days', n: days, tone: days > 3 ? 'ruby' : 'amber' };
};

export const WAITING_TONE_CLASSES = {
  slate: 'bg-n-slate-3 text-n-slate-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
};
